import React, { useEffect, useState } from "react";
import { Camera, Trophy, Upload, Check, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { WaveDivider } from "@/components/decor";
import ShareBar from "@/components/ShareBar";
import PupVoteGallery from "@/components/PupVoteGallery";
import PupRecaps from "@/components/PupRecaps";
import PupCountdown from "@/components/PupCountdown";
import PupCampaignKit from "@/components/PupCampaignKit";
import PupRules from "@/components/PupRules";
import CrownRevealBanner from "@/components/redcarpet/CrownRevealBanner";
import RedCarpetNav from "@/components/redcarpet/RedCarpetNav";

const toUrl = (p) => (p && p.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p);

function EntryForm({ onDone }) {
  const [f, setF] = useState({ pup_name: "", owner_name: "", email: "", caption: "", photo: "" });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/pup/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, photo: data.url }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.photo) { toast.error("Add a photo of your pup first 📸"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pup/enter", { ...f, consent: true });
      toast.success(data.message);
      onDone({ ...f, id: data.id, slug: data.slug, url: data.url });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const input = "w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FF6B9D] text-[#19324A]";
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl soft-shadow p-6 sm:p-8 space-y-4" data-testid="pup-entry-form">
      <label className={`block rounded-3xl border-2 border-dashed ${f.photo ? "border-[#7ED957]" : "border-[#19324A]/20"} bg-[#FFF9EE] p-4 text-center cursor-pointer hover:border-[#FF6B9D] transition-colors`}>
        {f.photo ? (
          <img src={toUrl(f.photo)} alt="Your pup" className="mx-auto max-h-64 rounded-2xl object-cover" data-testid="pup-photo-preview" />
        ) : (
          <div className="py-8"><Upload className="w-8 h-8 mx-auto text-[#FF6B9D]" /><p className="font-display font-bold text-[#19324A] mt-2">{uploading ? "Uploading…" : "Upload your pup's photo"}</p><p className="text-xs text-[#19324A]/55">JPG, PNG or WEBP · up to 8 MB</p></div>
        )}
        <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="pup-photo-input" />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required value={f.pup_name} onChange={set("pup_name")} placeholder="Pup's name" maxLength={40} className={input} data-testid="pup-name" />
        <input required value={f.owner_name} onChange={set("owner_name")} placeholder="Your name" maxLength={60} className={input} data-testid="pup-owner" />
      </div>
      <input required type="email" value={f.email} onChange={set("email")} placeholder="you@email.com (so we can tell you if you win)" className={input} data-testid="pup-email" />
      <div>
        <input value={f.caption} onChange={set("caption")} placeholder="One-line caption, e.g. “Professional biscuit inspector since 2021”" maxLength={140} className={input} data-testid="pup-caption" />
        <p className="text-[11px] text-[#19324A]/45 mt-1 text-right">{f.caption.length}/140</p>
      </div>
      <button type="submit" disabled={busy || uploading} className="btn-pink w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pup-submit">
        <Trophy className="w-5 h-5" /> {busy ? "Entering…" : "Enter Pup of the Month"}
      </button>
      <p className="text-[11px] text-[#19324A]/50 text-center">By entering you give The Happy Bulldog Co. permission to share this photo on our website and social pages. Entries are reviewed before they appear. Up to 3 entries per month.</p>
    </form>
  );
}

export default function PupOfTheMonthPage() {
  useSeo("Pup of the Month", "Enter your Bulldog in The Happy Bulldog Co. Pup of the Month — the winner stars on our homepage and scores free shipping.");
  const [data, setData] = useState(null);
  const [entered, setEntered] = useState(null);
  const reload = () => api.get("/pup/winner").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const w = data?.winner;

  return (
    <div className="min-h-screen bg-[#FFF9EE]" data-testid="pup-page">
      <section className="bg-[#FFD93D] relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-20 text-center">
          <p className="inline-flex items-center gap-2 bg-[#19324A] text-white text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full"><Trophy className="w-3.5 h-3.5 text-[#FFD93D]" /> Community</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#19324A] mt-4">Pup of the Month</h1>
          <div className="mt-4 flex justify-center"><RedCarpetNav active="vote" light /></div>
          <p className="text-[#19324A]/80 text-base md:text-lg mt-3 max-w-2xl mx-auto">One magnificent Bulldog, crowned every month by verified hearts. The winner stars on our homepage, gets a Winner's Spotlight with Chewie & Solo, and scores <strong>free shipping</strong> on their next order.</p>
          {data?.closes_at && (
            <div className="mt-6 inline-flex flex-col items-center gap-2 bg-white/70 rounded-3xl px-5 py-4" data-testid="pup-hero-countdown">
              <p className="text-xs font-bold uppercase tracking-wider text-[#19324A]/70">{data.month_label} entries & voting close in</p>
              <PupCountdown closesAt={data.closes_at} serverNow={data.server_now} />
              <p className="text-[11px] text-[#19324A]/60">11:59pm Brisbane time, {data.closes_on} · winner announced on the 1st</p>
            </div>
          )}
          {data?.pending_result && (
            <p className="mt-4 inline-block bg-[#19324A] text-[#FFD93D] font-display font-extrabold text-sm px-5 py-2.5 rounded-full" data-testid="pup-result-pending">{data.pending_result.month_label}: Official Result Pending — Chewie & Solo Are Consulting Their Calculators! 🧮🐾</p>
          )}
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10"><CrownRevealBanner compact /></div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-20 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-start">
        <div className="space-y-6">
          {w ? (
            <div className="bg-white rounded-3xl soft-shadow p-5" data-testid="pup-page-winner">
              <div className="relative">
                <img src={toUrl(w.photo)} alt={w.pup_name} className="w-full aspect-square object-cover rounded-2xl" />
                <span className="absolute top-3 left-3 bg-[#FFD93D] text-[#19324A] font-display font-extrabold text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {w.month_label}</span>
              </div>
              <h2 className="font-display font-extrabold text-2xl text-[#19324A] mt-4">Meet {w.pup_name} 🏆</h2>
              {w.caption && <p className="text-[#19324A]/75 italic mt-1">“{w.caption}”</p>}
              <p className="text-xs text-[#19324A]/50 mt-1">Shared by {w.owner_name}{w.final_votes != null && <span className="ml-2 font-bold text-[#FF6B9D]" data-testid="pup-winner-final-votes">❤️ {w.final_votes} verified hearts</span>}</p>
              {w.tie_break && <p className="text-xs mt-2 rounded-2xl bg-[#FFF9EE] p-2.5 text-[#19324A]/75" data-testid="pup-winner-tiebreak">🧮 A dead heat on hearts! This result was decided by Chewie & Solo using <a href="/downloads/Canine-Rectilinear-Matrices-and-Wrinkle-Criteria.pdf" target="_blank" rel="noopener noreferrer" className="underline font-bold">their official arbitration guide</a> — wrinkle matrices, suspicious equations and a very thorough photo-bombing-cat inspection.</p>}
              <p className="text-xs mt-2 text-[#19324A]/60">{w.pup_name} has been invited to Chewie & Solo's Winner's Spotlight interview 🎤🐾 · <a href={w.url} className="underline font-bold" data-testid="pup-winner-page-link">Winner's page</a></p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <ShareBar title={`${w.pup_name} is The Happy Bulldog Co. Pup of the Month 🏆`} path="/api/pup/share" testid="pup-page-share" />
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pup/${w.id}/share-image.png`} download={`pup-of-the-month-${w.pup_name}.png`} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display font-bold bg-[#FFD93D] text-[#19324A] soft-shadow hover:scale-105 transition-transform" data-testid="pup-page-download-card"><Download className="w-4 h-4" /> Square card</a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pup/${w.id}/share-story.png`} download={`pup-of-the-month-${w.pup_name}-story.png`} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display font-bold bg-[#9B59B6] text-white soft-shadow hover:scale-105 transition-transform" data-testid="pup-page-download-story"><Smartphone className="w-4 h-4" /> Story card</a>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl soft-shadow p-8 text-center" data-testid="pup-page-empty">
              <Camera className="w-12 h-12 mx-auto text-[#FF6B9D]" />
              <h2 className="font-display font-extrabold text-2xl text-[#19324A] mt-3">The crown is waiting</h2>
              <p className="text-[#19324A]/65 text-sm mt-1">No winner yet for {data?.month_label || "this month"} — your Bulldog could be the first.</p>
            </div>
          )}
          {data?.gallery?.length > 0 && <PupVoteGallery data={data} onChange={reload} />}
          {data?.past_winners?.length > 0 && (
            <div className="bg-white rounded-3xl soft-shadow p-5" data-testid="pup-past-winners">
              <h3 className="font-display font-extrabold text-lg text-[#19324A] mb-1">Hall of fame</h3>
              <p className="text-xs text-[#19324A]/55 mb-3">Tap the download icon to grab any past winner's share card.</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {data.past_winners.map((e) => (
                  <div key={e.id} className="shrink-0 w-28 text-center" data-testid={`pup-hof-${e.id}`}>
                    <div className="relative">
                      <img src={toUrl(e.photo)} alt={e.pup_name} className="w-28 h-28 object-cover rounded-2xl" />
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pup/${e.id}/share-image.png`} download={`pup-of-the-month-${e.pup_name}.png`} title={`Download ${e.pup_name}'s share card`} aria-label={`Download ${e.pup_name}'s share card`} className="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-[#FFD93D] text-[#19324A] inline-flex items-center justify-center soft-shadow hover:scale-110 transition-transform" data-testid={`pup-hof-download-${e.id}`}><Download className="w-4 h-4" /></a>
                    </div>
                    <p className="text-xs font-bold text-[#19324A] mt-1 truncate">{e.pup_name}</p>
                    <p className="text-[10px] text-[#19324A]/50">{e.month_label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <PupRecaps />
        </div>

        <div>
          {entered ? (
            <div className="bg-white rounded-3xl soft-shadow p-6 sm:p-8" data-testid="pup-entered">
              <div className="text-center">
                <span className="w-16 h-16 rounded-full bg-[#7ED957] inline-flex items-center justify-center"><Check className="w-8 h-8 text-[#19324A]" /></span>
                <h2 className="font-display font-extrabold text-2xl text-[#19324A] mt-4">🎉 YOUR PUP'S OFFICIAL CAMPAIGN CARD IS READY! 🐾👑</h2>
                <p className="text-[#19324A]/75 text-sm mt-3 leading-relaxed">Your Bulldog is officially in the running for The Happy Bulldog Co™ Pup of the Month crown!<br />We've rolled out the red carpet and created your very own personalised voting card!<br />Share it far and wide, rally your Bulldog-loving friends and family, and get those HEARTS rolling in! ❤️<br /><strong>Remember, every verified HEART brings your pup one paw closer to the crown!</strong></p>
                <p className="text-xs text-[#19324A]/55 mt-3">Entries are reviewed before they go live — the download buttons switch on the moment {entered.pup_name} is approved, and {entered.email} gets the cards by email too.</p>
              </div>
              <div className="mt-5"><PupCampaignKit entry={entered} pending stacked /></div>
              <button type="button" onClick={() => setEntered(null)} className="btn-outline mt-6 !py-2.5 w-full" data-testid="pup-enter-another">Enter another pup</button>
            </div>
          ) : (
            <>
              <h2 className="font-display font-extrabold text-2xl text-[#19324A] mb-3">Enter your Bulldog</h2>
              <EntryForm onDone={setEntered} />
            </>
          )}
          <div className="mt-6"><PupRules pdfReady={!!data?.arbitration_pdf_ready} /></div>
        </div>
      </div>
    </div>
  );
}
